import React, { useEffect, useRef, useState } from 'react';
import { publicAds, adClickTracked, adImpressionTracked, resolveFileUrl } from '../lib/api';
import { fetchVast, firePixel } from '../lib/vast';
import { Megaphone, X, ExternalLink } from 'lucide-react';

// Pre-roll ad. Supports 3 modes:
//   1. VAST (ad has vast_url)       -> plays video ad from external ad server
//   2. External HTML (external_html)-> renders sponsor HTML (e.g. AdSense snippet)
//   3. House ad (headline/body/img) -> our built-in pre-roll
export default function PrerollAd({ category, videoId, onDone }) {
  const [ad, setAd] = useState(null);
  const [vast, setVast] = useState(null);         // {mediaUrl, clickThrough, ...}
  const [seconds, setSeconds] = useState(5);
  const [done, setDone] = useState(false);
  const [adFailed, setAdFailed] = useState(false);
  const videoRef = useRef(null);
  const firedImpRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await publicAds({ placement: 'preroll', category, limit: 5 });
        if (cancelled) return;
        if (!list || !list.length) { finish(); return; }
        const chosen = list[Math.floor(Math.random() * list.length)];
        setAd(chosen);
        if (chosen.vast_url) {
          const parsed = await fetchVast(chosen.vast_url);
          if (cancelled) return;
          if (parsed && parsed.mediaUrl) {
            setVast(parsed);
            if (parsed.duration && parsed.duration > 0) setSeconds(Math.min(parsed.duration, 30));
          } else {
            // VAST failed — fall back to house display style
            setAdFailed(true);
          }
        }
        // impression (server-side earnings + counter)
        adImpressionTracked(chosen.id, videoId);
        firedImpRef.current = true;
      } catch { finish(); }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line
  }, [category, videoId]);

  // Fire VAST impression pixels once we have them
  useEffect(() => {
    if (!vast || !vast.impressions) return;
    vast.impressions.forEach(firePixel);
  }, [vast]);

  // Skip countdown (non-VAST + fallback modes)
  useEffect(() => {
    if (!ad || done) return;
    if (vast) return; // VAST uses video's own duration / end event
    if (seconds <= 0) return;
    const t = setTimeout(() => setSeconds(s => s - 1), 1000);
    return () => clearTimeout(t);
  }, [seconds, ad, done, vast]);

  // VAST countdown from video currentTime
  useEffect(() => {
    if (!vast || !videoRef.current) return;
    const el = videoRef.current;
    const onTime = () => {
      if (!el.duration) return;
      const left = Math.max(0, Math.round(el.duration - el.currentTime));
      setSeconds(left);
    };
    const onEnded = () => finish();
    el.addEventListener('timeupdate', onTime);
    el.addEventListener('ended', onEnded);
    el.play().catch(() => {});
    return () => { el.removeEventListener('timeupdate', onTime); el.removeEventListener('ended', onEnded); };
    // eslint-disable-next-line
  }, [vast]);

  const finish = () => { setDone(true); onDone?.(); };
  const handleClick = () => {
    if (ad) adClickTracked(ad.id, videoId);
    if (vast && vast.clickThrough) {
      vast.clickTracking?.forEach(firePixel);
      window.open(vast.clickThrough, '_blank', 'noopener');
    } else if (ad?.link_url) {
      window.open(ad.link_url, '_blank', 'noopener');
    }
  };

  if (!ad || done) return null;

  // --- VAST mode ---
  if (vast && !adFailed) {
    return (
      <div className="relative w-full aspect-video rounded-xl overflow-hidden bg-black" data-testid="preroll-vast">
        <video ref={videoRef} src={vast.mediaUrl} className="absolute inset-0 w-full h-full object-contain"
               controls={false} playsInline autoPlay muted={false} onClick={handleClick} />
        <div className="absolute top-3 left-3 flex items-center gap-1.5 text-[11px] uppercase tracking-wider bg-black/60 border border-white/10 rounded-full px-2.5 py-1">
          <Megaphone className="w-3 h-3 text-[#ffcf52]" /> Ad • Pre-roll
        </div>
        {vast.clickThrough && (
          <button onClick={handleClick} className="absolute bottom-3 left-3 btn-primary text-[12.5px]">
            Learn more <ExternalLink className="w-3.5 h-3.5" />
          </button>
        )}
        {seconds > 0 ? (
          <div className="absolute top-3 right-3 text-[12.5px] bg-black/60 border border-white/10 rounded-full px-3 py-1 text-white/85">
            Ad ends in {seconds}s
          </div>
        ) : (
          <button onClick={finish} className="absolute top-3 right-3 inline-flex items-center gap-1.5 text-[13px] bg-white text-black font-semibold rounded-full px-3 py-1.5 hover:bg-white/90" data-testid="preroll-skip">
            Skip ad <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
    );
  }

  // --- External HTML mode (e.g. partner banner for 5s) ---
  if (ad.external_html && !adFailed) {
    return (
      <div className="relative w-full aspect-video rounded-xl overflow-hidden bg-black" data-testid="preroll-external">
        <div className="absolute inset-0 grid place-items-center p-6"
             dangerouslySetInnerHTML={{ __html: ad.external_html }} />
        <div className="absolute top-3 left-3 flex items-center gap-1.5 text-[11px] uppercase tracking-wider bg-black/60 border border-white/10 rounded-full px-2.5 py-1">
          <Megaphone className="w-3 h-3 text-[#ffcf52]" /> Ad • Sponsored
        </div>
        {seconds > 0 ? (
          <div className="absolute top-3 right-3 text-[12.5px] bg-black/60 border border-white/10 rounded-full px-3 py-1 text-white/85">
            Skip in {seconds}s
          </div>
        ) : (
          <button onClick={finish} className="absolute top-3 right-3 inline-flex items-center gap-1.5 text-[13px] bg-white text-black font-semibold rounded-full px-3 py-1.5 hover:bg-white/90" data-testid="preroll-skip">
            Skip ad <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
    );
  }

  // --- House creative fallback ---
  return (
    <div className="relative w-full aspect-video rounded-xl overflow-hidden bg-black" data-testid="preroll-house">
      <img src={resolveFileUrl(ad.media_url) || 'https://images.unsplash.com/photo-1504384308090-c894fdcc538d?w=1600'} alt={ad.headline} className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0" style={{ background: 'linear-gradient(0deg, rgba(0,0,0,0.85), rgba(0,0,0,0.25) 60%)' }} />
      <div className="absolute top-3 left-3 flex items-center gap-1.5 text-[11px] uppercase tracking-wider bg-black/60 border border-white/10 rounded-full px-2.5 py-1">
        <Megaphone className="w-3 h-3 text-[#ffcf52]" /> Ad • Pre-roll
      </div>
      <div className="absolute inset-x-0 bottom-0 p-6 text-white">
        <div className="font-display text-[28px] md:text-[40px] leading-tight">{ad.headline || 'Sponsored'}</div>
        {ad.body && <p className="text-white/85 mt-1 text-[14px] max-w-[620px]">{ad.body}</p>}
        {ad.link_url && (
          <button onClick={handleClick} className="mt-3 btn-primary inline-flex">
            Visit site <ExternalLink className="w-4 h-4" />
          </button>
        )}
      </div>
      {seconds > 0 ? (
        <div className="absolute top-3 right-3 text-[12.5px] bg-black/60 border border-white/10 rounded-full px-3 py-1 text-white/85">
          Skip in {seconds}s
        </div>
      ) : (
        <button onClick={finish} className="absolute top-3 right-3 inline-flex items-center gap-1.5 text-[13px] bg-white text-black font-semibold rounded-full px-3 py-1.5 hover:bg-white/90" data-testid="preroll-skip">
          Skip ad <X className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  );
}
